import { useMutation } from '@tanstack/react-query';
import { Button, Link, Spinner, SystemIcon, TextField, Typography } from '@volvo/vcdk-react';
import { useCallback, useEffect, useRef } from 'react';
import { type Dispatch, type SetStateAction } from 'react';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import type { IUnitBySerialNumber } from '@/service/vpc/types/IUnitBySerialNumber';
import type { IIndustrialForm, IMarineForm, IUnitBaseRegistration } from '@/service/vpc/types/IUnitRegistration';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import {
  StepEnum,
  type ISegmentData,
  type StepEnum as StepEnumType,
} from '@/components/modals/unit-registration/AddNewUnitModal';
import useAnalytics from '@/contexts/analytics/useAnalytics';
import { useFleetAccount } from '@/contexts/fleet-account/useFleetAccount';
import { vpcService } from '@/service/vpc/vpcService';
import { dealerLocatorLink } from '@/utils/baseLinks';

interface IFormInput {
  serialNumber: string;
}

type SearchUnitContentProps = {
  initialSerialNumber?: string;
  autoSearch?: boolean;
  setUnitNotExistInProductCenter: Dispatch<SetStateAction<boolean>>;
  setSegment: Dispatch<SetStateAction<Partial<ISegmentData>>>;
  setDefaultValues: Dispatch<SetStateAction<Partial<IUnitBaseRegistration>>>;
  setDefaultValuesMarine: Dispatch<SetStateAction<Partial<IMarineForm>>>;
  setDefaultValuesIndustrial: Dispatch<SetStateAction<Partial<IIndustrialForm>>>;
  onCancel: () => void;
  onNext: Dispatch<SetStateAction<StepEnumType>>;
};

type ResultStatus =
  | 'valid'
  | 'maximum-reached'
  | 'already-added'
  | 'not-found'
  | 'not-registered-marine'
  | 'not-registered-industrial'
  | null;

const SearchUnitContent: React.FC<SearchUnitContentProps> = ({
  initialSerialNumber,
  autoSearch = false,
  setUnitNotExistInProductCenter,
  setSegment,
  setDefaultValues,
  setDefaultValuesMarine,
  setDefaultValuesIndustrial,
  onCancel,
  onNext,
}) => {
  const { t } = useTranslation();
  const { fleetAccountId } = useFleetAccount();
  const hasAutoSearched = useRef(false);
  const { analyticsEvents } = useAnalytics();
  const { selectedFleetAccount, fleetAccountUnits } = useFleetAccount();

  const { handleSubmit, getValues, control } = useForm<IFormInput>({
    defaultValues: {
      serialNumber: initialSerialNumber || '', // 1016115644 // 2016090993 // 2016090992
    },
  });

  const {
    data,
    mutateAsync: fetchUnitBySerialNumber,
    isPending,
    reset,
  } = useMutation({
    mutationFn: async ({ serialNumber, fleetAccountId }: { serialNumber: string; fleetAccountId: string }) => {
      return vpcService.Units.getUnitBySerialNumber({ serialNumber, fleetAccountId });
    },
    onSuccess(data) {
      console.log('data', data);

      const status = getResultStatus(data);
      console.log('status', status);
      const mainSegment = data?.unit?.mainSegment || data.product?.mainSegment.description || 'Unknown';
      const segment = data?.unit?.consumerLineOfBusiness || 'Unknown';
      const segmentDetail = data?.unit?.consumerUnitPurpose || 'Unknown';

      switch (status) {
        case 'valid':
          analyticsEvents.UnitRegistration.unitSearch({
            companyName: selectedFleetAccount?.name || '',
            mainSegment,
            segment,
            segmentDetail,
            maximumReached: false,
            alreadyAdded: false,
            unitNotExistInProductCenter: false,
          });
          break;
        case 'already-added':
          analyticsEvents.UnitRegistration.unitSearch({
            companyName: selectedFleetAccount?.name || '',
            mainSegment,
            segment,
            segmentDetail,
            maximumReached: false,
            alreadyAdded: true,
            unitNotExistInProductCenter: false,
          });
          break;
        case 'maximum-reached':
          analyticsEvents.UnitRegistration.unitSearch({
            companyName: selectedFleetAccount?.name || '',
            mainSegment,
            segment,
            segmentDetail,
            maximumReached: true,
            alreadyAdded: false,
            unitNotExistInProductCenter: false,
          });
          break;
        case 'not-registered-marine':
        case 'not-registered-industrial':
          analyticsEvents.UnitRegistration.unitSearch({
            companyName: selectedFleetAccount?.name || '',
            mainSegment,
            segment,
            segmentDetail,
            maximumReached: false,
            alreadyAdded: false,
            unitNotExistInProductCenter: true,
          });
          break;
        case 'not-found':
          analyticsEvents.UnitRegistration.unitSearch({
            companyName: selectedFleetAccount?.name || '',
            mainSegment,
            segment,
            segmentDetail,
            maximumReached: false,
            alreadyAdded: false,
            unitNotExistInProductCenter: true,
          });
          break;
        default:
          break;
      }

      setSegment({ mainSegment, segment, segmentDetail });

      if (data && data.unit !== null) {
        setUnitNotExistInProductCenter(false);
      } else {
        setUnitNotExistInProductCenter(true);
      }
    },
  });

  const onSubmit: SubmitHandler<IFormInput> = useCallback(
    async (formdata) => {
      await fetchUnitBySerialNumber({
        serialNumber: formdata.serialNumber.trim().toUpperCase(),
        fleetAccountId: fleetAccountId || '',
      });
    },
    [fetchUnitBySerialNumber, fleetAccountId]
  );

  // Auto-search when autoSearch prop is true and we have initial serial number
  useEffect(() => {
    if (autoSearch && initialSerialNumber && fleetAccountId && !hasAutoSearched.current) {
      hasAutoSearched.current = true;
      handleSubmit(onSubmit)();
    }
  }, [autoSearch, initialSerialNumber, fleetAccountId, handleSubmit, onSubmit]);

  const handleOnNext = (resultStatus: ResultStatus) => {
    const unit = data?.unit;
    const product = data?.product;

    if (resultStatus === 'not-registered-industrial' && product) {
      // UX decision (AB#1614521): every individual product that is not attached to a unit goes through
      // the registration process. The product-only payload carries no segment, so the request takes the
      // industrial contact-information path and support completes the registration.
      setDefaultValues((prev) => ({
        ...(prev ?? {}),
        engineSerials: [getValues('serialNumber')],
        manufacturer: '',
        model: '',
        vesselSegment: '',
        typeOfVessel: '',
      }));
      setDefaultValuesIndustrial({ machineId: '' });
      onNext(StepEnum.INDUSTRIAL_CONTACT_INFORMATION);
      return;
    }

    if (resultStatus !== 'valid') return;

    if (unit) {
      setDefaultValues((prev) => ({
        ...(prev ?? {}),
        engineSerials: [getValues('serialNumber')],
        manufacturer: unit.brand || '',
        model: unit.unitModel || '',
        vesselSegment: unit.consumerLineOfBusiness || '',
        typeOfVessel: unit.consumerUnitPurpose || '',
      }));

      if (unit.mainSegment === 'Marine') {
        if (unit.imoNumber) {
          setDefaultValuesMarine({
            vesselId: 'imoNumber',
            vesselValue: unit.imoNumber,
          });
        } else if (unit.mmsiNumber) {
          setDefaultValuesMarine({
            vesselId: 'mmsiNumber',
            vesselValue: unit.mmsiNumber,
          });
        }
        // Proceed to marine unit information step
        onNext(StepEnum.MARINE_UNIT_INFORMATION);
      } else {
        setDefaultValuesIndustrial({
          machineId: '',
        });
        // Handle industrial unit case if needed
        onNext(StepEnum.INDUSTRIAL_CONTACT_INFORMATION);
      }
    }
  };

  // Determine the response state
  const getResultStatus = (data: IUnitBySerialNumber | undefined): ResultStatus => {
    if (!data) return null;

    const hasProduct = data && 'product' in data && data.product;
    const hasUnit = data && 'unit' in data && data.unit;

    if (hasUnit && fleetAccountUnits?.some((u) => u.endCustomerUnitId === data.unit?.endCustomerUnitId))
      return 'already-added';

    const numberOfClaimedMarineUnits =
      fleetAccountUnits?.filter((u) => u.claiming && u.mainSegment === 'Marine').length || 0;
    if (hasUnit && data.unit?.mainSegment === 'Marine' && numberOfClaimedMarineUnits >= 3) return 'maximum-reached';

    if (!hasUnit && hasProduct && data.product?.mainSegment.shortName === 'M') return 'not-registered-marine';
    if (!hasUnit && hasProduct && data.product?.mainSegment.shortName === 'I') return 'not-registered-industrial';
    if (!hasProduct) return 'not-found';
    if (hasProduct && hasUnit) return 'valid';

    return null;
  };

  const resultStatus = getResultStatus(data);
  const isValidUnit = resultStatus === 'valid' || resultStatus === 'not-registered-industrial';

  return (
    <>
      <span slot="header">{t(($) => $.components.modal.add_new_unit_modal.search_unit.title)}</span>
      <div className="flex flex-col gap-8">
        <Typography>{t(($) => $.components.modal.add_new_unit_modal.search_unit.description)}</Typography>
        <div className="">
          <form className="flex items-end gap-6" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="serialNumber"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field }) => {
                return (
                  <TextField
                    label={t(($) => $.components.modal.add_new_unit_modal.search_unit.serial_number)}
                    className="w-full"
                    value={field.value}
                    onInput={(e) => {
                      reset();
                      return field.onChange(e.detail.value);
                    }}
                  />
                );
              }}
            />
            <Button variant="primary" type="submit" size="medium">
              <SystemIcon icon="search" invertTheme />
            </Button>
          </form>
          <Link
            variant="standalone-discreet"
            href="https://www.volvopenta.com/your-engine/find-your-serial-number/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Typography variant="caption2" className="text-subtle underline">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.find_serial_number)}
            </Typography>
          </Link>
        </div>

        {isPending && (
          <div className="bg-base flex items-center gap-5 rounded px-6 py-5">
            <Spinner size="small" />
            <Typography>{t(($) => $.common.loading)}</Typography>
          </div>
        )}

        {resultStatus === 'valid' && (
          <div className="bg-base flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-subtle">
              {data!.product?.productName.name} - {data!.unit?.mainSegment}
            </Typography>
            <p className="typography-body font-bold">{data!.unit?.brand}</p>
            <Typography variant="button" className="text-subtle">
              {data!.unit?.unitModel}
            </Typography>
          </div>
        )}

        {resultStatus === 'not-found' && (
          <div className="bg-error-subtle flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-error">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_not_found.label)}
            </Typography>
            <p className="typography-body text-error font-bold">Product not found</p>
            <Typography variant="button" className="text-error">
              <Trans
                i18nKey={($) => $.components.modal.add_new_unit_modal.search_unit.is_not_found.body}
                components={{
                  a: <a href={dealerLocatorLink} className="underline" target="_blank" rel="noopener noreferrer" />,
                }}
              />
            </Typography>
          </div>
        )}
        {resultStatus === 'not-registered-industrial' && (
          <div className="bg-base flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-subtle">
              {data!.product?.productName.name} - {data!.product?.mainSegment.description}
            </Typography>
            <p className="typography-body font-bold">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_not_registered_industrial.label)}
            </p>
            <Typography variant="button" className="text-subtle">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_not_registered_industrial.body)}
            </Typography>
          </div>
        )}

        {resultStatus === 'not-registered-marine' && (
          <div className="bg-error-subtle flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-error">
              {data!.product?.productName.name} - {data!.product?.mainSegment.description}
            </Typography>
            <p className="typography-body text-error font-bold">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_not_registered_marine.label)}
            </p>
            <Typography variant="button" className="text-error">
              <Trans
                i18nKey={($) => $.components.modal.add_new_unit_modal.search_unit.is_not_registered_marine.body}
                components={{
                  a: <a href={dealerLocatorLink} className="underline" target="_blank" rel="noopener noreferrer" />,
                }}
              />
            </Typography>
          </div>
        )}

        {resultStatus === 'already-added' && (
          <div className="bg-error-subtle flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-error">
              {data!.product?.productName.name} - {data!.unit?.mainSegment}
            </Typography>
            <p className="typography-body text-error font-bold">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_unit_already_added.label)}
            </p>
            <Typography variant="button" className="text-error">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_unit_already_added.body)}
            </Typography>
          </div>
        )}
        {resultStatus === 'maximum-reached' && (
          <div className="bg-error-subtle flex flex-col rounded px-6 py-5">
            <Typography variant="caption2" className="text-error">
              {data!.product?.productName.name}
            </Typography>
            <p className="typography-body text-error font-bold">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_maximum_units_reached.label)}
            </p>
            <Typography variant="button" className="text-error">
              {t(($) => $.components.modal.add_new_unit_modal.search_unit.is_maximum_units_reached.body)}
            </Typography>
          </div>
        )}
      </div>
      <span slot="modalFooter">
        <div className="flex gap-4">
          <Button variant="secondary" disabled={isPending} onClick={onCancel}>
            {t(($) => $.common.cancel)}
          </Button>
          <Button variant="primary" onClick={() => handleOnNext(resultStatus)} disabled={isPending || !isValidUnit}>
            {t(($) => $.common.continue)}
          </Button>
        </div>
      </span>
    </>
  );
};

export default withComponentErrorBoundary(SearchUnitContent);
